import { useState } from "react";
import { Link } from "react-router-dom";
import { Code2, Copy, Check, Globe, ShoppingBasket, CalendarDays } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { PUBLIC_URL } from "@/lib/publicUrl";

const Snippet = ({ code, testid }) => {
  const [copied, setCopied] = useState(false);
  const copy = () => { navigator.clipboard.writeText(code); setCopied(true); setTimeout(() => setCopied(false), 1500); toast.success("Codice copiato"); };
  return (
    <div className="relative">
      <pre className="rounded-lg bg-gray-950 text-gray-100 text-xs p-3 pr-12 overflow-x-auto whitespace-pre-wrap break-all" data-testid={testid}>{code}</pre>
      <Button variant="ghost" size="icon" onClick={copy} className="absolute top-1.5 right-1.5 h-8 w-8 text-gray-300 hover:text-white hover:bg-white/10" data-testid={`${testid}-copy`}>{copied ? <Check className="h-4 w-4 text-[#8fd39c]" /> : <Copy className="h-4 w-4" />}</Button>
    </div>
  );
};

export const EmbedCard = ({ slug, name, ops }) => {
  const origin = PUBLIC_URL;
  const widget = `<script src="${origin}/api/embed/${slug}.js" async></script>`;
  const iframe = `<iframe src="${origin}/${slug}?embed=1" title="Menù ${name}" style="width:100%;max-width:480px;height:800px;border:0;border-radius:24px;box-shadow:0 10px 40px rgba(0,0,0,.15)" loading="lazy" allow="payment"></iframe>`;
  const ordina = `<script src="${origin}/api/embed/${slug}/ordina.js" async></script>`;
  const prenota = `<script src="${origin}/api/embed/${slug}/prenota.js" async></script>`;
  const prenotaFrame = `<iframe src="${origin}/${slug}?embed=1&mode=prenota" title="Prenota un tavolo · ${name}" style="width:100%;max-width:480px;height:800px;border:0;border-radius:24px;box-shadow:0 10px 40px rgba(0,0,0,.15)" loading="lazy"></iframe>`;
  return (
    <div className="rounded-2xl mo-card p-6" data-testid="embed-card">
      <div className="flex items-center gap-2 mb-1"><Globe className="h-5 w-5 text-[#5aa469]" /><h3 className="font-display font-semibold text-2xl">Menù sul tuo sito web</h3></div>
      <p className="text-sm text-gray-500 mb-5">Incolla uno di questi codici nel tuo sito (WordPress, Wix, Squarespace, HTML): il menù è sempre aggiornato in automatico, ordini e pagamenti inclusi.</p>
      <div className="space-y-5">
        <div>
          <p className="text-sm font-semibold mb-1.5 inline-flex items-center gap-2"><Code2 className="h-4 w-4 text-gray-400" />Pulsante "Menù" flottante <span className="text-xs font-normal text-gray-400">(consigliato · una riga, funziona ovunque)</span></p>
          <Snippet code={widget} testid="embed-widget-code" />
          <p className="text-xs text-gray-400 mt-1">Aggiunge in basso a destra un pulsante con il colore del tuo menù che lo apre in sovrimpressione sul sito.</p>
        </div>
        <div>
          <p className="text-sm font-semibold mb-1.5 inline-flex items-center gap-2"><Code2 className="h-4 w-4 text-gray-400" />Menù incorporato nella pagina</p>
          <Snippet code={iframe} testid="embed-iframe-code" />
          <p className="text-xs text-gray-400 mt-1">Mostra il menù direttamente dentro una pagina del sito (es. la pagina "Menù").</p>
        </div>
        <div className="rounded-xl border border-[#5aa469]/30 bg-[#5aa469]/5 p-4 space-y-4" data-testid="embed-ops">
          <div className="flex items-center gap-2"><ShoppingBasket className="h-4 w-4 text-[#4a8a56]" /><p className="text-sm font-semibold">Ordini e prenotazioni sul tuo sito</p><span className="text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-full bg-[#1f2a24] text-white">Business</span></div>
          {ops ? (
            <>
              <p className="text-xs text-gray-500">I clienti ordinano l'asporto e prenotano il tavolo direttamente dal tuo sito: gli ordini arrivano in Ordini/Comande e le prenotazioni nel Calendario, con la stessa disponibilità del QR.</p>
              <div>
                <p className="text-sm font-semibold mb-1.5 inline-flex items-center gap-2"><Code2 className="h-4 w-4 text-gray-400" />Pulsante "Ordina online" (asporto)</p>
                <Snippet code={ordina} testid="embed-ordina-code" />
                <p className="text-xs text-gray-400 mt-1">Funziona se l'asporto è attivo nelle Impostazioni.</p>
              </div>
              <div>
                <p className="text-sm font-semibold mb-1.5 inline-flex items-center gap-2"><CalendarDays className="h-4 w-4 text-gray-400" />Pulsante "Prenota un tavolo"</p>
                <Snippet code={prenota} testid="embed-prenota-code" />
                <p className="text-xs text-gray-400 mt-1">Funziona se le prenotazioni tavolo sono attive. Puoi mettere entrambi i pulsanti: si posizionano uno sopra l'altro.</p>
              </div>
              <div>
                <p className="text-sm font-semibold mb-1.5 inline-flex items-center gap-2"><Code2 className="h-4 w-4 text-gray-400" />Modulo prenotazione incorporato nella pagina</p>
                <Snippet code={prenotaFrame} testid="embed-prenota-iframe-code" />
              </div>
            </>
          ) : (
            <p className="text-xs text-gray-500" data-testid="embed-ops-locked">Con il piano <strong>Business</strong> puoi installare sul tuo sito i pulsanti "Ordina online" e "Prenota un tavolo". <Link to="/dashboard/billing" className="text-[#4a8a56] underline">Passa a Business</Link></p>
          )}
        </div>
      </div>
    </div>
  );
};
